import React, { useState, useEffect } from "react";
import { FiChevronLeft, FiChevronRight, FiCopy } from "react-icons/fi";
import { useGetMyDepositsQuery } from "../../../features/bank-deposits/bankDepositSlice";
import { Loader } from "../../../shared/Loader";
import { formatDateWithTimeAndDate } from "../../../utils/formatDate";
import { NairaSymbol } from "../svgs/NairaSymbol";

interface DepositHistoryTableProps {
    page: number;
    onPageChange: (page: number) => void;
}

export const DepositHistoryTable: React.FC<DepositHistoryTableProps> = ({
    page,
    onPageChange,
}) => {
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const {
        data: deposits,
        isLoading,
        isFetching,
    } = useGetMyDepositsQuery({ page, limit: 10 });

    const copyToClipboard = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Task-list 5.0 T10: a deposit whose sender name did not match the
    // verified identity sits "under review" until an admin decides.
    const getStatusLabel = (status: string) =>
        status === 'under_review' ? 'Under review' : status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'credited':
                return 'text-green-400 bg-green-900/30';
            case 'pending':
                return 'text-yellow-400 bg-yellow-900/30';
            case 'under_review':
                return 'text-amber-400 bg-amber-900/30';
            case 'failed':
            case 'rejected':
                return 'text-red-400 bg-red-900/30';
            default:
                return 'text-gray-400 bg-gray-900/30';
        }
    };

    const formatAmount = (amount: number) => {
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
            minimumFractionDigits: 2,
        }).format(amount);
    };

    const truncateReference = (reference: string) => {
        if (reference.length <= 8) return reference;
        return `${reference.slice(0, 3)}...${reference.slice(-5)}`;
    };

    if (isLoading || isFetching) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader />
            </div>
        );
    }

    return (
        <div className="bg-black rounded-xl shadow-lg border border-gray-700">
            <div className="px-6 py-4 border-b border-gray-700">
                <h3 className="text-xl font-semibold text-white">Deposit History</h3>
                <p className="text-sm text-gray-400 mt-1">
                    View all your deposit transactions
                </p>
            </div>

            <div className="overflow-x-auto">
                {!deposits?.data || deposits.data.length === 0 ? (
                    <div className="w-full text-center py-12 text-gray-400">
                        No deposits found
                    </div>
                ) : (
                    <table className="w-full text-sm text-left text-gray-400">
                        <thead className="text-xs uppercase bg-gray-700 text-gray-300">
                            <tr>
                                <th scope="col" className="py-3 px-6">
                                    Reference
                                </th>
                                <th scope="col" className="py-3 px-6">
                                    Amount
                                </th>
                                <th scope="col" className="py-3 px-6">
                                    Credited Amount
                                </th>
                                <th scope="col" className="py-3 px-6">
                                    Fee
                                </th>
                                <th scope="col" className="py-3 px-6">
                                    Status
                                </th>
                                <th scope="col" className="py-3 px-6">
                                    Date
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {deposits.data.map((deposit) => (
                                <tr
                                    key={deposit._id}
                                    className="bg-black border-b border-gray-700 hover:bg-gray-700 transition-colors"
                                >
                                    <td className="py-4 px-6">
                                        <div className="flex items-center gap-2 relative">
                                            <span className="font-mono text-white" title={deposit.reference}>
                                                {truncateReference(deposit.reference)}
                                            </span>
                                            <button
                                                onClick={() => copyToClipboard(deposit.reference, deposit._id)}
                                                className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
                                            >
                                                <FiCopy className="w-4 h-4" />
                                            </button>
                                            {copiedId === deposit._id && (
                                                <span className="absolute -top-6 left-0 text-xs text-green-400 bg-gray-800 px-2 py-1 rounded whitespace-nowrap z-10">
                                                    Copied!
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-4 px-6">
                                        <div className="flex items-center gap-1">
                                            <NairaSymbol className="text-green-400" size={16} />
                                            <span className="text-white font-semibold">
                                                {formatAmount(deposit.amount)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="py-4 px-6">
                                        <div className="flex items-center gap-1">
                                            <NairaSymbol className="text-blue-400" size={16} />
                                            <span className="text-white">
                                                {formatAmount(deposit.creditedAmount || 0)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="py-4 px-6">
                                        <div className="flex items-center gap-1">
                                            <NairaSymbol className="text-red-400" size={16} />
                                            <span className="text-white">
                                                {formatAmount(deposit.fee || 0)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="py-4 px-6">
                                        <span
                                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                                deposit.status
                                            )}`}
                                        >
                                            {getStatusLabel(deposit.status)}
                                        </span>
                                    </td>
                                    <td className="py-4 px-6">
                                        <div className="text-white">
                                            {formatDateWithTimeAndDate(deposit.createdAt)}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination */}
            {deposits && deposits.data.length > 0 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-700">
                    <div className="text-sm text-gray-400">
                        Page {page} of {deposits.last_page} | Total: {deposits.total} deposits
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onPageChange(page - 1)}
                            disabled={page === 1}
                            className="rounded-full bg-gray-700 hover:bg-gray-600 text-white disabled:cursor-not-allowed disabled:bg-gray-700/50 disabled:text-gray-500 text-lg cursor-pointer p-3 transition-colors"
                        >
                            <FiChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => onPageChange(page + 1)}
                            disabled={page >= deposits.last_page}
                            className="rounded-full bg-gray-700 hover:bg-gray-600 text-white disabled:cursor-not-allowed disabled:bg-gray-700/50 disabled:text-gray-500 text-lg cursor-pointer p-3 transition-colors"
                        >
                            <FiChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
